/**
 * Structure cluster — rolling-window approximations of Smart-Money-Concepts
 * structural ideas.
 *
 * TRUE pivot detection, FVG discovery, and order-block marking require
 * dedicated node types that do not yet exist.  Every recipe below is an
 * APPROXIMATION using the nodes that ARE registered: rollingMaximum /
 * rollingMinimum, barOffset, barSize, wickSize, rollingAverage, ratio,
 * positionInRange, condition, merge, barsConditionCounter, entryRules.
 *
 * Each entry's `intent` field calls this out explicitly.
 *
 * Node-type and handle references were verified against:
 *   apps/frontend/src/features/backtest/schemas/node-schemas/
 */

import type { CookbookEntry } from './types'

// ── 2. Structure Break Entry ──────────────────────────────────
//
// Enter long when the structure-break proxy fires.
//
// Nodes: (same proxy chain) → Trade Manager
const structureBreakEntry: CookbookEntry = {
  key: 'structure-break-entry',
  name: 'Structure Break Entry (BOS proxy)',
  category: 'structure',
  intent:
    'Rolling-window approximation: enters long when close exceeds the 20-bar prior rolling maximum, used as a proxy for a bullish BOS entry.',
  summary:
    'Enters long on a close-confirmed break of the 20-bar rolling high, approximating a BOS entry.',
  difficulty: 'intermediate',
  tags: ['Breakout', 'Highs and lows'],
  whenToUse:
    'Use in established intraday uptrends where you want a breakout entry triggered by a confirmed close above recent structure rather than a wick poke.',
  whatToLookFor:
    'A bar that closes above the prior 20-bar rolling maximum. Using close (not high) reduces wick-fakeout entries, but the structural level is still an approximation — a true BOS requires confirmed swing pivots, not just a rolling extreme.',
  howItWorks:
    'The chain builds a lagged rolling maximum and routes price.close (not price.high) into the final condition node, requiring the bar to close above the lagged rolling maximum before the Trade Manager enters Long. This close-confirmation step reduces entries triggered by intrabar wicks that reverse before the bar closes. The rolling-maximum/barOffset chain still approximates the swing high rather than computing it from confirmed pivot geometry, so the level shifts continuously with each new bar.',
  recipe: [
    {
      id: 'price',
      type: 'priceData',
      params: { timeframe: '15m' },
    },
    {
      id: 'swing_high',
      type: 'rollingMaximum',
      params: { reference: 'xbars', period: 20 },
      inputs: { input_1: 'price.high' },
    },
    {
      id: 'prior_max',
      type: 'barOffset',
      params: { offset: 1 },
      inputs: { input_1: 'swing_high.maximum' },
    },
    {
      // Use close (not high) to confirm break before entry
      id: 'bos_confirm',
      type: 'condition',
      params: { operator: '>' },
      inputs: { input_1: 'price.close', input_2: 'prior_max.output' },
    },
  ],
  tradeManager: {
    params: { orderDirection: 'Long' },
    feeds: { socket: 'trigger', from: 'bos_confirm.true' },
  },
}

// ── 3. Liquidity Sweep ────────────────────────────────────────
//
// True sweep detection requires knowing a prior named level was tagged.
// APPROXIMATION: lower wick pierces the rolling N-bar minimum (prior low
// liquidity pool) but the bar closes back above it — both conditions merged
// with AND.
//
// Nodes:
//   priceData → rollingMinimum(xbars, 20) → barOffset(1) → condition(low < prior_min)  ┐
//   priceData → condition(close > prior_min)                                             ├ merge(AND)
const liquiditySweep: CookbookEntry = {
  key: 'liquidity-sweep',
  name: 'Liquidity Sweep (rolling low approximation)',
  category: 'structure',
  intent:
    'Rolling-window approximation of a bearish liquidity sweep: low pierces the 20-bar prior rolling minimum but close reclaims it. True named-level sweep detection is unavailable.',
  summary:
    'Identifies a potential bullish liquidity sweep: wick dips below the 20-bar rolling low but the bar closes back above it.',
  difficulty: 'advanced',
  tags: ['Reclaim', 'Highs and lows'],
  whenToUse:
    'Use when looking for a stop-hunt reversal setup at a rolling demand zone — price briefly takes out resting sell-stops below recent lows before buyers reassert. Most relevant in liquid intraday markets near a known support level.',
  whatToLookFor:
    'A bar whose low pierces the prior 20-bar minimum AND whose close reclaims above it in the same bar. The limitation is significant: this detects any wick-and-reclaim regardless of whether a named structural level or pool of resting orders actually existed there — the 20-bar rolling minimum is a shifting proxy, not a fixed order-flow level.',
  howItWorks:
    'A rollingMinimum node computes the 20-bar low-of-lows, standing in for the "liquidity pool" below recent price. barOffset(1) shifts it back one bar to prevent look-ahead. Two separate condition nodes check (1) price.low < prior_min (wick pierced the level) and (2) price.close > prior_min (close reclaimed above it). A merge(AND) node requires both conditions on the same bar, which is the structural signature of a sweep-and-reverse. True liquidity-sweep detection would require tracking named swing lows and confirming that stop orders were actually triggered — neither capability exists in the current node set.',
  recipe: [
    {
      id: 'price',
      type: 'priceData',
      params: { timeframe: '5m' },
    },
    {
      // 20-bar rolling minimum of the low — proxy for "liquidity pool"
      id: 'swing_low',
      type: 'rollingMinimum',
      params: { reference: 'xbars', period: 20 },
      inputs: { input_1: 'price.low' },
    },
    {
      // Shift back one bar so we compare current bar vs prior level
      id: 'prior_min',
      type: 'barOffset',
      params: { offset: 1 },
      inputs: { input_1: 'swing_low.minimum' },
    },
    {
      // Wick tagged the prior low
      id: 'wick_pierce',
      type: 'condition',
      params: { operator: '<' },
      inputs: { input_1: 'price.low', input_2: 'prior_min.output' },
    },
    {
      // Close reclaimed above prior low — bullish reclaim after sweep
      id: 'close_reclaim',
      type: 'condition',
      params: { operator: '>' },
      inputs: { input_1: 'price.close', input_2: 'prior_min.output' },
    },
    {
      // Both: wick pierced AND close reclaimed
      id: 'sweep_signal',
      type: 'merge',
      params: { operator: 'AND' },
      inputs: { input_1: 'wick_pierce.true', input_2: 'close_reclaim.true' },
    },
  ],
  tradeManager: { feeds: { socket: 'trigger', from: 'sweep_signal.result' } },
}

// ── 8. FVG Entry (Fair Value Gap midpoint approximation) ──────
//
// True FVG detection requires knowing that candle 3's low > candle 1's high
// (or vice versa): a gap between them.  APPROXIMATION: compute the midpoint
// of (high 2 bars ago + low 1 bar ago), treating it as the centre of a
// hypothetical gap region.  Enter long when the current close crosses above
// that midpoint, as if price is "filling into" the gap from below.
//
// Note: math nodes (add, divide) accept only `number` inputs. We pipe OHLCV
// field selections through barOffset so the validator sees a numeric passthrough.
//
// Nodes:
//   priceData → barOffset(2) [high 2 bars ago = candle-1 proxy top]
//   priceData → barOffset(1) [low 1 bar ago  = candle-2 proxy low]
//             → add(c1_high, c2_low) → divide(sum, 2) [gap midpoint]
//             → condition(close > midpoint) → Trade Manager
const fvgEntry: CookbookEntry = {
  key: 'fvg-entry',
  name: 'FVG Entry (midpoint approximation)',
  category: 'structure',
  intent:
    'Heavily approximate FVG proxy: fires when close rises above the midpoint of (high 2 bars ago + low 1 bar ago). Does NOT verify an actual price gap exists. True FVG detection is unavailable.',
  summary:
    'Approximates a bullish FVG entry by entering when close crosses above the midpoint of recent bar extremes.',
  difficulty: 'advanced',
  tags: ['Breakout', 'Fair value gap'],
  whenToUse:
    'Use after a strong bullish impulse candle as a speculative proxy for a "fill-into-the-gap" entry, expecting price to retrace slightly into the inefficiency before continuing. Be aware this is the least faithful approximation in the cluster.',
  whatToLookFor:
    "A bar that closes above the computed midpoint of (high 2 bars ago + low 1 bar ago). Critical limitation: a true FVG requires confirming that candle 3's low is strictly above candle 1's high, creating a visible price gap, which this recipe does NOT check. It will fire on every bar where close is above the recent high-low midpoint, gap or not.",
  howItWorks:
    "Two barOffset nodes capture price.high from 2 bars ago (proxy for candle 1's top boundary) and price.low from 1 bar ago (proxy for candle 3's bottom boundary). An add node sums those two values and a divide node halves the result, producing the midpoint of the hypothetical gap region. A condition node fires when the current close crosses above that midpoint, and the Trade Manager enters Long. The fundamental gap-existence check (candle 3 low > candle 1 high) is absent because the engine has no node that compares values across two different barOffset outputs in a single boolean; adding a gap-verification condition would require a dedicated FVG node type.",
  recipe: [
    {
      id: 'price',
      type: 'priceData',
      params: { timeframe: '5m' },
    },
    {
      // High of the candle 2 bars ago: proxy for candle-1 top of the gap.
      // barOffset passthrough output is typed number, compatible with add.
      id: 'c1_high',
      type: 'barOffset',
      params: { offset: 2 },
      inputs: { input_1: 'price.high' },
    },
    {
      // Low of 1 bar ago: proxy for candle-2 bottom, also typed number via passthrough.
      id: 'c2_low',
      type: 'barOffset',
      params: { offset: 1 },
      inputs: { input_1: 'price.low' },
    },
    {
      // Gap midpoint numerator = c1_high + c2_low
      id: 'gap_sum',
      type: 'add',
      inputs: { input_1: 'c1_high.output', input_2: 'c2_low.output' },
    },
    {
      // Divide by 2 to get midpoint
      id: 'midpoint',
      type: 'divide',
      inputs: { input_1: 'gap_sum.value', input_2: 2 },
    },
    {
      // Enter long when close crosses above the gap midpoint
      id: 'fvg_signal',
      type: 'condition',
      params: { operator: '>' },
      inputs: { input_1: 'price.close', input_2: 'midpoint.value' },
    },
  ],
  tradeManager: {
    params: { orderDirection: 'Long' },
    feeds: { socket: 'trigger', from: 'fvg_signal.true' },
  },
}

// ── Export ───────────────────────────────────────────────────

export const structureSeedRecipes: CookbookEntry[] = [structureBreakEntry, liquiditySweep, fvgEntry]
